import React from 'react';

/**
 * Drop target and file picker. `checkHeader` sees the first bytes before the rest is
 * read, so a wrong file is rejected without loading a whole export.
 */

/** Ample for a header row; a file with no line break in it matches no schema anyway. */
const HEADER_PREFLIGHT_BYTES = 64 * 1024;

export const CsvUpload = ({
  onFile,
  onReading,
  checkHeader,
  onReject,
  disabled
}: {
  onFile: (name: string, text: string) => void;
  /** Called as soon as a file is chosen, before any of it has been read. */
  onReading?: (name: string) => void;
  checkHeader?: (head: string) => string | undefined;
  onReject?: (name: string, head: string) => void;
  disabled?: boolean;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);

  const read = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    onReading?.(file.name);
    if (checkHeader) {
      const head = await file.slice(0, HEADER_PREFLIGHT_BYTES).text();
      const problem = checkHeader(head);
      if (problem) {
        onReject?.(file.name, head);
        return;
      }
    }
    onFile(file.name, await file.text());
  };

  return (
    <div
      className={`rounded-lg border border-dashed p-6 text-center ${
        dragging
          ? 'border-sky-400 bg-sky-50 dark:border-sky-500 dark:bg-sky-500/10'
          : 'border-neutral-300 dark:border-neutral-600'
      } ${disabled ? 'opacity-50' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (!disabled) {
          read(event.dataTransfer.files[0]);
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          read(event.currentTarget.files?.[0]);
          // Clearing lets the same file be chosen again after a failed attempt.
          event.currentTarget.value = '';
        }}
      />
      <p className="text-sm text-neutral-700 dark:text-neutral-200">
        Drop a CSV here, or{' '}
        <button
          type="button"
          className="font-medium text-sky-600 underline disabled:no-underline dark:text-sky-400"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          choose a file
        </button>
      </p>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        Start from the template so the columns match.
      </p>
    </div>
  );
};
